import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Clock, PiggyBank, ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = ({ onStartForm }) => {
  const [debtAmount, setDebtAmount] = useState(15000);
  const [calculatorResult, setCalculatorResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const calculateDebounced = setTimeout(() => {
      fetchCalculation();
    }, 300);
    return () => clearTimeout(calculateDebounced);
  }, [debtAmount]);

  const fetchCalculation = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/calculator`, {
        debt_amount: debtAmount,
        monthly_income: 2000,
        number_of_creditors: 3
      });
      setCalculatorResult(response.data);
    } catch (error) {
      console.error("Calculator error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section className="hero-background min-h-[90vh] relative overflow-hidden" data-testid="hero-section">
      <div className="relative z-10 container-main py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Value Proposition */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3">
              <div className="trust-badge" data-testid="trust-badge-regulated">
                <ShieldCheck className="w-4 h-4" />
                <span>FCA Regulated Partners</span>
              </div>
              <div className="trust-badge" data-testid="trust-badge-support">
                <Clock className="w-4 h-4" />
                <span>Free Introduction</span>
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] leading-[1.1] tracking-tight">
              Take Control of <br />
              <span className="text-[#10B981]">Your Debt</span> Today
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              Discover how an Individual Voluntary Arrangement (IVA) could reduce 
              your monthly payments and write off up to 85% of your unsecured debt.
            </p>

            {/* Key Benefits */}
            <div className="grid sm:grid-cols-3 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Up to 85%</p>
                  <p className="text-sm text-slate-500">Debt Written Off</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">5 Years</p>
                  <p className="text-sm text-slate-500">Fixed Term</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Protected</p>
                  <p className="text-sm text-slate-500">From Creditors</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Calculator Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="calculator-card p-6 md:p-8" data-testid="hero-calculator">
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-[#0F172A] mb-2">
                    How much do you owe?
                  </h2>
                  <p className="text-slate-500 text-sm">
                    Move the slider to see your potential savings
                  </p>
                </div>

                {/* Debt Amount Display */}
                <div className="text-center py-4">
                  <span className="font-numbers text-5xl md:text-6xl font-bold text-[#0F172A]" data-testid="debt-amount-display">
                    {formatCurrency(debtAmount)}
                  </span>
                </div>

                {/* Slider */}
                <div className="px-2">
                  <Slider
                    value={[debtAmount]}
                    onValueChange={(value) => setDebtAmount(value[0])}
                    min={5000}
                    max={75000}
                    step={500}
                    className="w-full"
                    data-testid="debt-slider"
                  />
                  <div className="flex justify-between mt-2 text-sm text-slate-400">
                    <span>£5,000</span>
                    <span>£75,000+</span>
                  </div>
                </div>

                {/* Results */}
                {calculatorResult && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-5 space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Estimated Monthly Payment</p>
                        <p className="font-numbers text-2xl font-bold text-[#0F172A]" data-testid="monthly-payment">
                          {formatCurrency(calculatorResult.estimated_iva_payment)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Monthly Savings</p>
                        <p className="font-numbers text-2xl font-bold text-[#10B981]" data-testid="monthly-savings">
                          {formatCurrency(calculatorResult.estimated_monthly_savings)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-emerald-200">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Potential Total Savings</p>
                      <p className="font-numbers text-3xl font-bold text-[#10B981]" data-testid="total-savings">
                        {formatCurrency(calculatorResult.estimated_total_savings)}*
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* CTA Button */}
                <Button
                  onClick={() => onStartForm(debtAmount)}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold py-6 text-lg rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-emerald-200"
                  data-testid="get-started-btn"
                >
                  Check Your Eligibility
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>

                <p className="text-xs text-slate-400 text-center">
                  *Subject to eligibility. Free, no-obligation assessment.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
